import { useState, useEffect, useRef, useMemo } from 'react';
import { useLazyListSiteEventsQuery } from '../redux/api/eventsApi';
import type { SiteEvent } from '../redux/api/eventsApi';
import type { PortfolioSite } from '../redux/api/portfolioApi';

export interface PortfolioEventsResult {
  events: SiteEvent[];
  isLoading: boolean;
  loadProgress: { current: number; total: number };
  /** Map from site_id → site_name for display */
  siteNameMap: Record<string, string>;
}

/**
 * Loads events from portfolio sites in sequential batches.
 *
 * @param maxSites - Cap on how many sites to query (default 20). Prevents
 *   flooding the network when portfolios have 50+ sites.
 * @param perSiteLimit - Events per site (default 20, max 100).
 *
 * NOTE: Ideally the backend should expose a portfolio-level events endpoint:
 *   GET /api/v1/portfolio/{portfolio_id}/events
 * to avoid N+1 queries. This hook is a client-side fan-out workaround.
 */
export function usePortfolioEvents(
  sites: PortfolioSite[],
  { enabled = true, perSiteLimit = 20, maxSites = 20 }: { enabled?: boolean; perSiteLimit?: number; maxSites?: number } = {}
): PortfolioEventsResult {
  const [fetchEvents] = useLazyListSiteEventsQuery();
  const [events, setEvents] = useState<SiteEvent[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadProgress, setLoadProgress] = useState({ current: 0, total: 0 });
  const loadedKeyRef = useRef('');
  const loadingRef = useRef(false);

  // Cap site count
  const cappedSites = useMemo(() => sites.slice(0, maxSites), [sites, maxSites]);

  // Stable key from site IDs
  const siteKey = useMemo(
    () => cappedSites.map(s => s.site_id || s.id || '').filter(Boolean).sort().join(','),
    [cappedSites]
  );

  // Build site name map (from ALL sites, not just capped)
  const siteNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const s of sites) {
      const id = s.site_id || s.id || '';
      if (id) map[id] = s.site_name || s.name || 'Unknown';
    }
    return map;
  }, [sites]);

  useEffect(() => {
    if (!enabled || !siteKey || loadingRef.current || loadedKeyRef.current === siteKey) return;

    loadingRef.current = true;
    setIsLoading(true);
    setLoadProgress({ current: 0, total: cappedSites.length });

    const load = async () => {
      const allEvents: SiteEvent[] = [];
      // Small batches (3) to avoid saturating browser connection limit (6 per host)
      const batchSize = 3;

      for (let i = 0; i < cappedSites.length; i += batchSize) {
        const batch = cappedSites.slice(i, i + batchSize);
        const results = await Promise.allSettled(
          batch.map(async (site) => {
            const siteId = site.site_id || site.id || '';
            if (!siteId) return [];
            try {
              const resp = await fetchEvents({
                siteId,
                limit: Math.min(perSiteLimit, 100),
                sort_by: 'occurred_at',
                sort_dir: 'desc',
              }).unwrap();
              return resp.items || [];
            } catch {
              return [];
            }
          })
        );
        for (const r of results) {
          if (r.status === 'fulfilled') allEvents.push(...r.value);
        }
        setLoadProgress({ current: Math.min(i + batchSize, cappedSites.length), total: cappedSites.length });
      }

      // Sort all events by date descending
      allEvents.sort((a, b) => new Date(b.occurred_at).getTime() - new Date(a.occurred_at).getTime());
      setEvents(allEvents);
      loadedKeyRef.current = siteKey;
      setIsLoading(false);
      loadingRef.current = false;
    };

    load();
  }, [enabled, siteKey, cappedSites, perSiteLimit, fetchEvents]);

  return { events, isLoading, loadProgress, siteNameMap };
}
